import { newAmount, newFinancialRate } from '@orus.eu/amount'

import type { DeductiblePart, InsuranceDeductible, PossibleInsuranceDeductible } from '../../../model/policy-item.ts'
import type { FrMrgaPolicyItem } from '../fr-mrga-insurance.ts'

// fr-mrga has no deductible selector, so a deductible offered as a choice among several values cannot be written here.
export type FrMrgaDeductible = Extract<
  PossibleInsuranceDeductible,
  {
    type:
      | 'fixedAmount'
      | 'none'
      | 'not-applicable'
      | 'fixedTimeInDays'
      | 'text'
      | 'interventionThresholdAmount'
      | 'interventionThresholdPercentage'
      | 'percentageWithMinimumAmount'
      | 'multipleDeductibles'
  }
>

type FrMrgaDeductibleEntry = { deductible: FrMrgaDeductible; additionalInfo: string | null }

type TextDeductible = Extract<InsuranceDeductible, { type: 'text' }>

const legalNaturalDisastersDeductible: TextDeductible = {
  type: 'text',
  value: 'Franchise légale du régime Catastrophes Naturelles',
}

const vehicleEventDeductible: TextDeductible = {
  type: 'text',
  value: '10 % des dommages, minimum 2 500 €, maximum 5 000 € par véhicule endommagé',
}

// Each vehicle under third party plus or all risks chooses its own all-accident damage deductible, 750, 1 500 or
// 2 500 €, in the fleet statement, so the contract points to it.
const fleetAllAccidentDamageDeductible: DeductiblePart = {
  label: 'Dommages tous accidents',
  deductible: { type: 'text', value: 'Selon mention de l’état de parc' },
}

const ownedVehiclesDeductible: Extract<InsuranceDeductible, { type: 'multipleDeductibles' }> = {
  type: 'multipleDeductibles',
  deductibles: [
    { label: 'Incendie, vol, attentats et émeutes', deductible: vehicleEventDeductible },
    fleetAllAccidentDamageDeductible,
    { label: 'Catastrophes naturelles', deductible: legalNaturalDisastersDeductible },
  ],
}

export const frMrgaDeductibleByPolicyItem_v1_0_0 = {
  publicLiability: {
    deductible: { type: 'fixedAmount', amount: newAmount(750) },
    additionalInfo: 'Par sous-garantie',
  },
  productsAndCompletedWorksLiability: {
    deductible: {
      type: 'multipleDeductibles',
      deductibles: [
        { label: 'Tous dommages confondus', deductible: { type: 'fixedAmount', value: newAmount(750) } },
        {
          label: 'Dommages matériels et immatériels consécutifs',
          deductible: { type: 'fixedAmount', value: newAmount(750) },
        },
        { label: 'Dommages immatériels non consécutifs', deductible: { type: 'fixedAmount', value: newAmount(3000) } },
      ],
    },
    additionalInfo: null,
  },
  fire: {
    deductible: {
      type: 'multipleDeductibles',
      deductibles: [
        {
          label: 'Bâtiment et contenu',
          deductible: {
            type: 'percentageWithMinimumAmount',
            value: newFinancialRate('0.15'),
            minimum: newAmount(1000),
            baseText: 'du coût des dommages',
          },
        },
        {
          label:
            'Dont matériels et mobilier professionnels, effets personnels des préposés, marchandises, supports d’informations, fonds et valeurs',
          deductible: { type: 'fixedAmount', value: newAmount(1000) },
        },
        { label: 'Frais et pertes, responsabilités', deductible: { type: 'fixedAmount', value: newAmount(1000) } },
      ],
    },
    // The agreed terms set a specific deductible per prevention measure: tyre storage, charging area, etc.
    additionalInfo:
      'En cas de non-respect des mesures de prévention des Conditions particulières, franchise spécifique selon la mesure non respectée, prévue aux Conditions particulières',
  },
  electricalDamage: { deductible: { type: 'fixedAmount', amount: newAmount(1500) }, additionalInfo: null },
  climateEvents: { deductible: { type: 'fixedAmount', amount: newAmount(1500) }, additionalInfo: null },
  naturalDisasters: { deductible: legalNaturalDisastersDeductible, additionalInfo: null },
  terrorism: { deductible: { type: 'fixedAmount', amount: newAmount(1500) }, additionalInfo: null },
  waterDamage: { deductible: { type: 'fixedAmount', amount: newAmount(1000) }, additionalInfo: null },
  buildingDamageAfterTheft: {
    deductible: {
      type: 'multipleDeductibles',
      deductibles: [
        {
          label: 'Frais de gardiennage et de clôture provisoire, remplacement des clés et serrures',
          deductible: { type: 'none' },
        },
        {
          label: 'Perte financière sur aménagements, honoraires d’expert',
          deductible: { type: 'fixedAmount', value: newAmount(1000) },
        },
      ],
    },
    additionalInfo: null,
  },
  theft: {
    deductible: {
      type: 'multipleDeductibles',
      deductibles: [
        { label: 'Contenu, honoraires d’expert', deductible: { type: 'fixedAmount', value: newAmount(1000) } },
        { label: 'Frais de gardiennage et de clôture provisoire', deductible: { type: 'none' } },
      ],
    },
    additionalInfo: null,
  },
  glass: {
    deductible: {
      type: 'multipleDeductibles',
      deductibles: [
        { label: 'Biens', deductible: { type: 'fixedAmount', value: newAmount(1000) } },
        { label: 'Frais et pertes', deductible: { type: 'none' } },
      ],
    },
    additionalInfo: null,
  },
  machineryAndComputerBreakage: {
    deductible: {
      type: 'multipleDeductibles',
      deductibles: [
        { label: 'Dommages matériels aux biens garantis', deductible: { type: 'fixedAmount', value: newAmount(1000) } },
        {
          label: 'Frais et pertes',
          deductible: {
            type: 'percentageWithMinimumAmount',
            value: newFinancialRate('0.1'),
            minimum: newAmount(250),
            baseText: 'du montant des dommages',
          },
        },
      ],
    },
    additionalInfo: null,
  },
  businessInterruption: { deductible: { type: 'fixedTimeInDays', value: 3 }, additionalInfo: null },
  civilLiability: { deductible: { type: 'none' }, additionalInfo: null },
  // The CP template words it as the threshold under which the insurer does not step in, not a deductible
  criminalDefenseAndRecourse: {
    deductible: { type: 'interventionThresholdAmount', value: newAmount(250), baseText: 'par litige' },
    additionalInfo: null,
  },
  driverProtection: {
    deductible: {
      type: 'interventionThresholdPercentage',
      value: newFinancialRate('0.1'),
      baseText: 'AIPP (atteinte à l’intégrité physique et psychique)',
    },
    additionalInfo: null,
  },
  ownedVehiclesDamage: { deductible: ownedVehiclesDeductible, additionalInfo: null },
  personalBelongingsAndAccessories: {
    deductible: { type: 'fixedAmount', amount: newAmount(750) },
    additionalInfo: null,
  },
  vehiclesForSaleDamage: { deductible: ownedVehiclesDeductible, additionalInfo: null },
  entrustedVehiclesDamage: {
    deductible: {
      type: 'multipleDeductibles',
      deductibles: [
        { label: 'Incendie, vol, attentats et émeutes, forces de la nature', deductible: vehicleEventDeductible },
        // TODO(INS-2156): the product sheet points to the fleet, which does not list the entrusted vehicles, so the
        // product team has to give this deductible.
        fleetAllAccidentDamageDeductible,
        { label: 'Catastrophes naturelles', deductible: legalNaturalDisastersDeductible },
      ],
    },
    additionalInfo: null,
  },
  legalProtection: {
    deductible: {
      type: 'multipleDeductibles',
      deductibles: [
        { label: 'Phase amiable', deductible: { type: 'none' } },
        {
          label: 'Phase judiciaire',
          deductible: { type: 'interventionThresholdAmount', value: newAmount(500), baseText: 'de montant en litige' },
        },
      ],
    },
    additionalInfo: 'Aucun seuil pour une consultation juridique ou une comparution devant une juridiction répressive',
  },
} as const satisfies Record<FrMrgaPolicyItem, FrMrgaDeductibleEntry>
